import { useEffect, useRef, useState } from 'react';
import { Check, Link2 } from 'lucide-react';
import { Logo } from '../../components/Logo';
import { cn } from '../../lib/utils';
import { prefersReducedMotion, usePlayOnView } from './motion';

// A brief, drawn as a sheet of paper, filling itself in as a client would from
// the shared link: each answer types in, then the brief is marked reviewed and
// the answers are merged in. Made-up answers, no client.

const TYPE_MS = 32; // per character
const PAUSE_MS = 350; // between answers

export function BriefFill({ money }) {
  const stageRef = useRef(null);
  const seen = usePlayOnView(stageRef, 0.4);
  const [instant] = useState(prefersReducedMotion);
  const fields = [
    ['What are we making?', 'A 30-second launch film'],
    ['When is the shoot?', 'Two days in March'],
    ['How should it feel?', 'Warm, golden, unhurried'],
    ['Rough budget', `${money(15000)} to ${money(20000)}`],
  ];
  const total = fields.reduce((n, [, answer]) => n + answer.length, 0);
  const [typed, setTyped] = useState(0); // characters typed across all answers
  const [reviewed, setReviewed] = useState(false);

  useEffect(() => {
    if (!seen) {
      if (!instant) {
        setTyped(0);
        setReviewed(false);
      }
      return undefined;
    }
    if (instant) {
      setTyped(total);
      setReviewed(true);
      return undefined;
    }
    // One timer per character, with a pause after each answer
    const timers = [];
    let t = 500;
    let count = 0;
    fields.forEach(([, answer]) => {
      for (let c = 0; c < answer.length; c += 1) {
        count += 1;
        const n = count;
        timers.push(setTimeout(() => setTyped(n), t));
        t += TYPE_MS;
      }
      t += PAUSE_MS;
    });
    timers.push(setTimeout(() => setReviewed(true), t + 200));
    return () => timers.forEach(clearTimeout);
  }, [seen, instant, total]);

  let before = 0;
  return (
    <div ref={stageRef} className="rounded-highlight bg-background border-2 border-foreground p-5 sm:p-6 flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Logo className="w-8 h-8 shrink-0" alt="" />
          <div className="min-w-0">
            <p className="text-lg leading-tight">Brief</p>
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Link2 className="w-3.5 h-3.5" /> Shared link, no account needed
            </p>
          </div>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-pill px-3 py-1 text-sm transition-colors duration-500',
            reviewed ? 'bg-moss-soft text-moss' : 'bg-mist text-foreground',
          )}
        >
          {reviewed ? 'Reviewed' : 'Sent'}
        </span>
      </div>

      <dl className="flex flex-col gap-3">
        {fields.map(([question, answer]) => {
          const shown = Math.max(0, Math.min(answer.length, typed - before));
          const typing = shown > 0 && shown < answer.length;
          before += answer.length;
          return (
            <div key={question} className="flex flex-col gap-1">
              <dt className="text-sm text-muted-foreground">{question}</dt>
              <dd className="min-h-10 rounded-cards bg-surface px-3 py-2 text-[15px]">
                {answer.slice(0, shown)}
                {typing && <span className="inline-block w-px h-4 align-middle bg-foreground ml-0.5 animate-pulse" aria-hidden="true" />}
              </dd>
            </div>
          );
        })}
      </dl>

      <p className={cn('flex items-center gap-2 text-sm transition-opacity duration-500', reviewed ? 'opacity-100 text-moss' : 'opacity-0')}>
        <Check className="w-4 h-4" /> Answers merged into the brief
      </p>
    </div>
  );
}
